/**
 * MicroappAvailabilityCard — entity-page availability card (IDP-887)
 *
 * Shows per-app infra/app uptime, calendar heat-map, legend, and recent
 * incidents for the selected microapp. Supports 30d / 60d / 90d period toggle.
 */

import { default as React, useState, useEffect } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import {
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Box,
  Typography,
  Chip,
  Collapse,
  IconButton,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DesktopMacIcon from '@mui/icons-material/DesktopMac';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { formatDuration } from './utils';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AppDailyBreakdown {
  date: string;
  infra_pct: number;
  app_pct: number;
  infra_status: 'operational' | 'degraded' | 'outage';
  app_status: 'operational' | 'degraded' | 'outage';
}

interface IncidentSignals {
  p1Pass?: boolean;
  p2Pass?: boolean;
  p3Pass?: boolean;
  gcpErrorSpike?: boolean;
  logDetail?: {
    errorSnippets: string[];
    logEntriesCount: number;
    fetchedAt: string;
  };
}

interface IncidentEntry {
  id: string;
  layer: string;
  root_cause: string;
  started_at: string;
  ended_at: string | null;
  duration_minutes: number | null;
  signals?: IncidentSignals;
}

interface AppSummaryResponse {
  app_name: string;
  period_days: number;
  actual_days_with_data: number;
  current_status: 'available' | 'degraded' | 'unavailable' | 'unknown';
  infra_uptime_pct: number;
  infra_total_downtime_minutes: number;
  app_uptime_pct: number;
  app_total_downtime_minutes: number;
  overall_uptime_pct: number;
  daily_breakdown: AppDailyBreakdown[];
  incidents: IncidentEntry[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PERIOD_OPTIONS = [30, 60, 90] as const;
type PeriodDays = (typeof PERIOD_OPTIONS)[number];

const STATUS_COLORS: Record<string, string> = {
  operational: '#4caf50',
  degraded: '#ff9800',
  outage: '#e53935',
};

const LAYER_CHIP_STYLES: Record<string, { bg: string; color: string }> = {
  application: { bg: '#e3f2fd', color: '#1565c0' },
  infrastructure: { bg: '#f3e5f5', color: '#6a1b9a' },
  both: { bg: '#e0f2f1', color: '#00695c' },
};

const ROOT_CAUSE_LABELS: Record<string, string> = {
  oauth2_proxy: 'OAuth2 proxy failure',
  istio: 'Network / ingress failure',
  upstream_connectivity:
    'App unreachable from proxy (wrong port, network policy, or no ready endpoints)',
  app_pod: 'Application pod failure',
  pod_restart: 'Pod crash (OOMKill / eviction)',
  zero_replicas: 'No replicas running',
  platform_wide: 'Platform-wide outage',
  gcp_error_spike: 'GCP error rate spike',
  flapping: 'Intermittent / flapping',
  secret_mount_failure:
    'Secret mount failure — grant secretmanager.secretAccessor to the pod service account on the referenced GCP project',
  stuck_rollout:
    'Deployment stuck — new pods cannot start (FailedMount, CrashLoopBackOff, or similar). Old pods are still serving traffic. Check ArgoCD health message and pod events for the root cause.',
};

const CARD_STATUS_CONFIG = {
  operational: { bg: '#e8f5e9', text: '#2e7d32', label: 'Operational' },
  degraded: { bg: '#fff8e1', text: '#e65100', label: 'Degraded' },
  outage: { bg: '#ffebee', text: '#c62828', label: 'Outage' },
} as const;

type CardStatusKey = keyof typeof CARD_STATUS_CONFIG;

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles(theme => ({
  loadingBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '160px',
  },
  noDataText: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    textAlign: 'center' as const,
  },
  metricLabel: {
    fontSize: '0.65rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    marginBottom: 2,
  },
  metricValue: {
    fontWeight: 700,
    lineHeight: 1.1,
  },
  metricSubtitle: {
    fontSize: '0.72rem',
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  periodBtn: {
    fontSize: '0.72rem',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 4,
    cursor: 'pointer',
    border: '1px solid #d0d0d0',
    background: 'transparent',
    color: '#555',
    fontFamily: 'inherit',
    lineHeight: '20px',
    '&:hover': { background: '#f5f7fa' },
  },
  periodBtnActive: {
    background: '#1976d2',
    color: '#fff',
    borderColor: '#1976d2',
    '&:hover': { background: '#1565c0' },
  },
  calendarRow: {
    marginTop: theme.spacing(1.5),
  },
  calendarLabel: {
    fontSize: '0.65rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    marginBottom: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  squaresRow: {
    display: 'flex',
    gap: 2,
  },
  square: {
    flex: 1,
    minWidth: 4,
    height: 22,
    borderRadius: 2,
    cursor: 'default',
  },
  dateLabelsRow: {
    display: 'flex',
    gap: 2,
    marginTop: 3,
    marginBottom: theme.spacing(1),
  },
  dateLabel: {
    flex: 1,
    minWidth: 4,
    fontSize: 9,
    color: '#888',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
  },
  legend: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(0.5),
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: '0.72rem',
    color: theme.palette.text.secondary,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    display: 'inline-block',
    flexShrink: 0,
  },
  sectionTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    marginBottom: theme.spacing(0.75),
    color: theme.palette.text.primary,
  },
  tableHeaderCell: {
    fontSize: '0.65rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase' as const,
    color: theme.palette.text.secondary,
    padding: '6px 8px',
    borderBottom: '1px solid #e0e0e0',
  },
  tableBodyCell: {
    fontSize: '0.8rem',
    padding: '6px 8px',
    borderBottom: '1px solid #f5f5f5',
  },
  statusChip: {
    fontWeight: 600,
    fontSize: '0.72rem',
    height: '22px',
    marginTop: '12px',
    marginRight: '8px',
  },
  divider: {
    margin: theme.spacing(1.5, 0),
    borderTop: '1px solid #e0e0e0',
  },
}));

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatIncidentDate = (dateStr: string): string =>
  new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const formatShortDate = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
};

const formatRootCause = (cause: string): string =>
  ROOT_CAUSE_LABELS[cause] ??
  cause.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const PROBE_STATUS_TO_CARD: Record<string, CardStatusKey> = {
  available: 'operational',
  degraded: 'degraded',
  unavailable: 'outage',
  // 'unknown' intentionally omitted — no chip rendered when status is unknown
};

const deriveStatus = (summary: AppSummaryResponse): CardStatusKey | undefined =>
  PROBE_STATUS_TO_CARD[summary.current_status];

// ─── Sub-components ───────────────────────────────────────────────────────────

interface CalendarRowProps {
  days: AppDailyBreakdown[];
  colorKey: 'infra_status' | 'app_status';
  pctKey: 'infra_pct' | 'app_pct';
  label: string;
  dotColor: string;
  classes: ReturnType<typeof useStyles>;
}

const CalendarHeatRow = ({
  days,
  colorKey,
  pctKey,
  label,
  dotColor,
  classes,
}: CalendarRowProps) => (
  <Box className={classes.calendarRow}>
    <Typography className={classes.calendarLabel} style={{ color: dotColor }}>
      <span>●</span>&nbsp;{label}
    </Typography>
    <Box className={classes.squaresRow}>
      {days.map(day => (
        <Tooltip
          key={day.date}
          title={`${formatShortDate(day.date)}: ${day[colorKey]} (${day[
            pctKey
          ].toFixed(1)}%)`}
          arrow
        >
          <Box
            className={classes.square}
            style={{
              backgroundColor: STATUS_COLORS[day[colorKey]] ?? '#e0e0e0',
            }}
          />
        </Tooltip>
      ))}
    </Box>
  </Box>
);

// ─── Component ────────────────────────────────────────────────────────────────

export const MicroappAvailabilityCard = () => {
  const { entity } = useEntity();
  const classes = useStyles();
  const config = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [period, setPeriod] = useState<PeriodDays>(30);
  const [summary, setSummary] = useState<AppSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [expandedIncidentId, setExpandedIncidentId] = useState<string | null>(
    null,
  );

  const appName = entity.metadata.name;

  useEffect(() => {
    let cancelled = false;
    const fetchSummary = async () => {
      try {
        setLoading(true);
        setFetchError(false);
        const backendUrl = config.getString('backend.baseUrl');
        const response = await fetchApi.fetch(
          `${backendUrl}/api/catalog-api/availability/${encodeURIComponent(
            appName,
          )}/summary?days=${period}`,
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data: AppSummaryResponse = await response.json();
        if (!cancelled) setSummary(data);
      } catch {
        if (!cancelled) setFetchError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchSummary();
    return () => {
      cancelled = true;
    };
  }, [appName, config, fetchApi, period]);

  const statusKey: CardStatusKey | null =
    summary && summary.actual_days_with_data > 0
      ? deriveStatus(summary) ?? null
      : null;
  const statusConfig = statusKey ? CARD_STATUS_CONFIG[statusKey] : null;

  const sortedIncidents = summary
    ? [...summary.incidents].sort(
        (a, b) =>
          new Date(b.started_at).getTime() - new Date(a.started_at).getTime(),
      )
    : [];

  const anyExpandable = sortedIncidents.some(i => {
    const s = i.signals ?? {};
    return (
      s.p1Pass !== undefined ||
      s.p2Pass !== undefined ||
      s.p3Pass !== undefined ||
      s.gcpErrorSpike !== undefined ||
      !!s.logDetail
    );
  });

  return (
    <Card>
      <CardHeader
        title={
          <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DesktopMacIcon style={{ fontSize: 20, color: '#555' }} />
            <Typography variant="h6">Availability</Typography>
          </Box>
        }
        action={
          statusConfig && (
            <Chip
              label={statusConfig.label}
              size="small"
              className={classes.statusChip}
              data-testid="availability-status-chip"
              style={{
                backgroundColor: statusConfig.bg,
                color: statusConfig.text,
              }}
            />
          )
        }
      />

      <CardContent>
        {loading && (
          <Box className={classes.loadingBox}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading &&
          (fetchError || !summary || summary.actual_days_with_data === 0) && (
            <Box className={classes.loadingBox}>
              <Typography className={classes.noDataText}>
                No availability data yet — data is collected every 5 minutes
              </Typography>
            </Box>
          )}

        {!loading &&
          !fetchError &&
          summary &&
          summary.actual_days_with_data > 0 && (
            <>
              {/* ── Metric stats + period toggle ── */}
              <Box
                style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}
              >
                {/* Infra uptime */}
                <Box style={{ flex: 1 }}>
                  <Typography
                    className={classes.metricLabel}
                    style={{ color: '#1976d2' }}
                  >
                    ● Infrastructure Uptime
                  </Typography>
                  <Box
                    style={{ display: 'flex', alignItems: 'baseline', gap: 2 }}
                  >
                    <Typography
                      className={classes.metricValue}
                      style={{ fontSize: '2rem' }}
                    >
                      {summary.infra_uptime_pct.toFixed(2)}
                    </Typography>
                    <Typography
                      className={classes.metricValue}
                      style={{ fontSize: '1rem' }}
                    >
                      %
                    </Typography>
                  </Box>
                  <Typography className={classes.metricSubtitle}>
                    Last {period}d &middot;{' '}
                    <span
                      style={{
                        color:
                          summary.infra_total_downtime_minutes > 0
                            ? '#1565c0'
                            : 'inherit',
                        fontWeight:
                          summary.infra_total_downtime_minutes > 0 ? 600 : 400,
                      }}
                    >
                      {formatDuration(summary.infra_total_downtime_minutes)}{' '}
                      downtime
                    </span>
                  </Typography>
                </Box>

                {/* App uptime */}
                <Box style={{ flex: 1 }}>
                  <Typography
                    className={classes.metricLabel}
                    style={{ color: '#7b1fa2' }}
                  >
                    ● Application Uptime
                  </Typography>
                  <Box
                    style={{ display: 'flex', alignItems: 'baseline', gap: 2 }}
                  >
                    <Typography
                      className={classes.metricValue}
                      style={{ fontSize: '2rem' }}
                    >
                      {summary.app_uptime_pct.toFixed(2)}
                    </Typography>
                    <Typography
                      className={classes.metricValue}
                      style={{ fontSize: '1rem' }}
                    >
                      %
                    </Typography>
                  </Box>
                  <Typography className={classes.metricSubtitle}>
                    Last {period}d &middot;{' '}
                    <span
                      style={{
                        color:
                          summary.app_total_downtime_minutes > 0
                            ? '#7b1fa2'
                            : 'inherit',
                        fontWeight:
                          summary.app_total_downtime_minutes > 0 ? 600 : 400,
                      }}
                    >
                      {formatDuration(summary.app_total_downtime_minutes)}{' '}
                      downtime
                    </span>
                  </Typography>
                </Box>

                {/* Period toggle */}
                <Box style={{ display: 'flex', gap: 4, paddingTop: 2 }}>
                  {PERIOD_OPTIONS.map(d => (
                    <button
                      key={d}
                      type="button"
                      className={`${classes.periodBtn}${
                        period === d ? ` ${classes.periodBtnActive}` : ''
                      }`}
                      onClick={() => setPeriod(d)}
                    >
                      {d}d
                    </button>
                  ))}
                </Box>
              </Box>

              {/* ── Calendar heat-map ── */}
              <CalendarHeatRow
                days={summary.daily_breakdown}
                colorKey="infra_status"
                pctKey="infra_pct"
                label="Infrastructure"
                dotColor="#1976d2"
                classes={classes}
              />
              <CalendarHeatRow
                days={summary.daily_breakdown}
                colorKey="app_status"
                pctKey="app_pct"
                label="Application"
                dotColor="#7b1fa2"
                classes={classes}
              />

              {/* Date labels — every 5th day */}
              <Box className={classes.dateLabelsRow}>
                {summary.daily_breakdown.map((day, idx) => (
                  <Box key={day.date} className={classes.dateLabel}>
                    {idx % 5 === 0 ? formatShortDate(day.date) : ''}
                  </Box>
                ))}
              </Box>

              {/* ── Legend ── */}
              <Box className={classes.legend}>
                {(['operational', 'degraded', 'outage'] as const).map(
                  status => (
                    <Box key={status} className={classes.legendItem}>
                      <Box
                        className={classes.legendDot}
                        style={{ backgroundColor: STATUS_COLORS[status] }}
                      />
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Box>
                  ),
                )}
              </Box>

              {/* ── Recent Incidents ── */}
              <Box className={classes.divider} />
              <Typography className={classes.sectionTitle}>
                Recent Incidents
              </Typography>

              {sortedIncidents.length === 0 ? (
                <Typography
                  style={{ fontSize: '0.8rem', color: '#888', marginTop: 4 }}
                >
                  No incidents in the last {period} days
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHeaderCell}>
                        Date
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        Duration
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        Layer
                      </TableCell>
                      <TableCell className={classes.tableHeaderCell}>
                        Cause
                      </TableCell>
                      {anyExpandable && (
                        <TableCell
                          className={classes.tableHeaderCell}
                          style={{ width: 32, padding: '6px 4px' }}
                        />
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedIncidents.slice(0, 10).map(incident => {
                      const layerStyle =
                        LAYER_CHIP_STYLES[incident.layer] ??
                        LAYER_CHIP_STYLES.infrastructure;
                      const isExpanded = expandedIncidentId === incident.id;
                      const signals = incident.signals ?? {};
                      const logDetail = signals.logDetail;
                      const hasDetails =
                        signals.p1Pass !== undefined ||
                        signals.p2Pass !== undefined ||
                        signals.p3Pass !== undefined ||
                        signals.gcpErrorSpike !== undefined;

                      return (
                        <React.Fragment key={incident.id}>
                          <TableRow
                            hover
                            style={{
                              cursor:
                                hasDetails || logDetail ? 'pointer' : 'default',
                            }}
                            onClick={() => {
                              if (hasDetails || logDetail) {
                                setExpandedIncidentId(
                                  isExpanded ? null : incident.id,
                                );
                              }
                            }}
                          >
                            <TableCell className={classes.tableBodyCell}>
                              {formatIncidentDate(incident.started_at)}
                            </TableCell>
                            <TableCell className={classes.tableBodyCell}>
                              {formatDuration(incident.duration_minutes)}
                            </TableCell>
                            <TableCell className={classes.tableBodyCell}>
                              <Chip
                                label={
                                  incident.layer.charAt(0).toUpperCase() +
                                  incident.layer.slice(1)
                                }
                                size="small"
                                style={{
                                  backgroundColor: layerStyle.bg,
                                  color: layerStyle.color,
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  height: 20,
                                }}
                              />
                            </TableCell>
                            <TableCell className={classes.tableBodyCell}>
                              {formatRootCause(incident.root_cause)}
                            </TableCell>
                            {anyExpandable && (
                              <TableCell
                                className={classes.tableBodyCell}
                                style={{ width: 32, padding: '0 4px' }}
                              >
                                {(hasDetails || logDetail) && (
                                  <IconButton
                                    size="small"
                                    aria-label={
                                      isExpanded
                                        ? 'hide details'
                                        : 'show details'
                                    }
                                  >
                                    {isExpanded ? (
                                      <KeyboardArrowUpIcon
                                        style={{ fontSize: 18 }}
                                      />
                                    ) : (
                                      <KeyboardArrowDownIcon
                                        style={{ fontSize: 18 }}
                                      />
                                    )}
                                  </IconButton>
                                )}
                              </TableCell>
                            )}
                          </TableRow>

                          <TableRow>
                            <TableCell
                              colSpan={anyExpandable ? 5 : 4}
                              style={{ padding: 0, border: 0 }}
                            >
                              <Collapse in={isExpanded} unmountOnExit>
                                <Box
                                  style={{
                                    padding: '8px 12px',
                                    background: '#f9f9fb',
                                    borderBottom: '1px solid #e0e0e0',
                                  }}
                                >
                                  {/* Probe signal indicators */}
                                  {hasDetails && (
                                    <Box
                                      style={{
                                        display: 'flex',
                                        flexWrap: 'wrap' as const,
                                        gap: 10,
                                        marginBottom: 6,
                                      }}
                                    >
                                      <Typography
                                        style={{
                                          fontSize: '0.7rem',
                                          fontWeight: 700,
                                          color: '#555',
                                          alignSelf: 'center',
                                        }}
                                      >
                                        Probe signals:
                                      </Typography>
                                      {(
                                        [
                                          ['p1Pass', 'P1 HTTP'],
                                          ['p2Pass', 'P2 OAuth proxy'],
                                          ['p3Pass', 'P3 App pod'],
                                        ] as const
                                      ).map(([key, label]) => {
                                        const val =
                                          signals[
                                            key as
                                              | 'p1Pass'
                                              | 'p2Pass'
                                              | 'p3Pass'
                                          ];
                                        if (val === undefined) return null;
                                        return (
                                          <Box
                                            key={key}
                                            style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 4,
                                            }}
                                          >
                                            <Box
                                              style={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                background: val
                                                  ? '#4caf50'
                                                  : '#e53935',
                                                flexShrink: 0,
                                              }}
                                            />
                                            <Typography
                                              style={{ fontSize: '0.7rem' }}
                                            >
                                              {label}: {val ? 'OK' : 'Failed'}
                                            </Typography>
                                          </Box>
                                        );
                                      })}
                                      {signals.gcpErrorSpike !== undefined && (
                                        <Box
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 4,
                                          }}
                                        >
                                          <Box
                                            style={{
                                              width: 8,
                                              height: 8,
                                              borderRadius: '50%',
                                              background: signals.gcpErrorSpike
                                                ? '#e53935'
                                                : '#4caf50',
                                              flexShrink: 0,
                                            }}
                                          />
                                          <Typography
                                            style={{ fontSize: '0.7rem' }}
                                          >
                                            GCP errors:{' '}
                                            {signals.gcpErrorSpike
                                              ? 'Spike detected'
                                              : 'Normal'}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  )}

                                  {/* Error log snippets */}
                                  {logDetail &&
                                    logDetail.errorSnippets.length > 0 && (
                                      <Box style={{ marginBottom: 6 }}>
                                        <Typography
                                          style={{
                                            fontSize: '0.7rem',
                                            fontWeight: 700,
                                            color: '#555',
                                            marginBottom: 4,
                                          }}
                                        >
                                          Error logs captured at incident open (
                                          {logDetail.logEntriesCount} entries):
                                        </Typography>
                                        {logDetail.errorSnippets
                                          .slice(0, 3)
                                          .map((snippet, idx) => (
                                            <Box
                                              key={idx}
                                              style={{
                                                fontSize: '0.68rem',
                                                fontFamily: 'monospace',
                                                background: '#fff3e0',
                                                padding: '4px 6px',
                                                marginBottom: 3,
                                                borderRadius: 2,
                                                borderLeft: '3px solid #ff9800',
                                                whiteSpace: 'pre-wrap' as const,
                                                wordBreak: 'break-all' as const,
                                                maxHeight: 80,
                                                overflow: 'auto',
                                              }}
                                            >
                                              {snippet}
                                            </Box>
                                          ))}
                                        {logDetail.errorSnippets.length > 3 && (
                                          <Typography
                                            style={{
                                              fontSize: '0.68rem',
                                              color: '#888',
                                            }}
                                          >
                                            +
                                            {logDetail.errorSnippets.length - 3}{' '}
                                            more entries
                                          </Typography>
                                        )}
                                      </Box>
                                    )}

                                  {!hasDetails && !logDetail && (
                                    <Typography
                                      style={{
                                        fontSize: '0.7rem',
                                        color: '#888',
                                      }}
                                    >
                                      No diagnostic signals captured for this
                                      incident.
                                    </Typography>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </>
          )}
      </CardContent>
    </Card>
  );
};
